/**
 * ReviewTranscriptPanel - 文字起こし（左）+ 指摘一覧（右）の2カラム
 *
 * ManagerReviewView の review モード Step 3 相当を、単一セッション向けに切り出したもの。
 * 指摘 ⇔ 文字起こしハイライトの連動（主発言=赤、補助発言=amber、選択中のみ補助を光らせる）を実装。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ViewModeToggle, type ViewMode } from '@/components/review/ViewModeToggle';
import {
  TranscriptTimeline,
  type TranscriptTimelineHandle,
} from '@/components/review/TranscriptTimeline';
import {
  reviewTranscriptBlocksToTimeline,
  type ReviewFeedbackItem,
  type ReviewSessionDetailResponse,
  type Utterance,
} from '@/types/review';

interface ReviewTranscriptPanelProps {
  detail: ReviewSessionDetailResponse;
}

export function ReviewTranscriptPanel({ detail }: ReviewTranscriptPanelProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('full');
  const [selectedFeedbackKey, setSelectedFeedbackKey] = useState<string | null>(
    detail.session_feedback_items[0]?.feedback_key ?? null,
  );
  const [selectedUtteranceId, setSelectedUtteranceId] = useState<string | undefined>(
    detail.session_feedback_items[0]?.primary_utterance_id ??
      detail.session_feedback_items[0]?.utterance_id ??
      undefined,
  );

  const timelineRef = useRef<TranscriptTimelineHandle>(null);

  // 時系列順 feedback
  const feedbackItems = useMemo<ReviewFeedbackItem[]>(() => {
    return [...detail.session_feedback_items].sort((a, b) => {
      const ai = a.sequence_index ?? 0;
      const bi = b.sequence_index ?? 0;
      if (ai !== bi) return ai - bi;
      return a.timestamp.localeCompare(b.timestamp);
    });
  }, [detail.session_feedback_items]);

  const timelineBlocks = useMemo(
    () => reviewTranscriptBlocksToTimeline(detail.transcript_blocks, detail.session_feedback_items),
    [detail.transcript_blocks, detail.session_feedback_items],
  );

  // utterance_id → ReviewFeedbackItem[]（primary/supporting 双方を含む。発話クリック→カード選択）
  const feedbackByUtterance = useMemo(() => {
    const map = new Map<string, ReviewFeedbackItem[]>();
    const pushUnique = (utteranceId: string, item: ReviewFeedbackItem) => {
      const existing = map.get(utteranceId);
      if (!existing) {
        map.set(utteranceId, [item]);
        return;
      }
      if (!existing.some((e) => e.feedback_key === item.feedback_key)) existing.push(item);
    };
    for (const item of feedbackItems) {
      const primary = item.primary_utterance_id || item.utterance_id;
      if (primary) pushUnique(primary, item);
      for (const supportingId of item.supporting_utterance_ids) {
        if (!supportingId || supportingId === primary) continue;
        pushUnique(supportingId, item);
      }
    }
    return map;
  }, [feedbackItems]);

  // 選択中 feedback の supporting_utterance_ids（Set 化: includes を避けパフォーマンス向上）
  const activeSupportingUtteranceIds = useMemo<Set<string>>(() => {
    if (!selectedFeedbackKey) return new Set();
    const match = feedbackItems.find((item) => item.feedback_key === selectedFeedbackKey);
    return new Set(match?.supporting_utterance_ids ?? []);
  }, [selectedFeedbackKey, feedbackItems]);

  // feedback_key → 1-indexed 番号
  const feedbackIndexByKey = useMemo(() => {
    const map = new Map<string, number>();
    feedbackItems.forEach((item, i) => map.set(item.feedback_key, i + 1));
    return map;
  }, [feedbackItems]);

  // utterance_id → 指摘番号配列（primary / supporting 双方から番号を拾う）
  const feedbackIndicesByUtteranceId = useMemo(() => {
    const map = new Map<string, number[]>();
    const push = (utteranceId: string, n: number) => {
      const existing = map.get(utteranceId) ?? [];
      if (!existing.includes(n)) existing.push(n);
      map.set(utteranceId, existing);
    };
    feedbackItems.forEach((item, i) => {
      const n = i + 1;
      const primary = item.primary_utterance_id || item.utterance_id;
      if (primary) push(primary, n);
      for (const supportingId of item.supporting_utterance_ids) {
        if (!supportingId || supportingId === primary) continue;
        push(supportingId, n);
      }
    });
    return map;
  }, [feedbackItems]);

  // session 切替時の選択状態リセット（同 instance に別 detail が入ったとき旧 key が残る問題対策）
  const sessionId = detail.session.session_id;
  useEffect(() => {
    const first = feedbackItems[0];
    setSelectedFeedbackKey(first?.feedback_key ?? null);
    setSelectedUtteranceId(first?.primary_utterance_id ?? first?.utterance_id ?? undefined);
    // sessionId が変わった時だけ実行（feedbackItems は同 session 内で都度 sort される）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleFeedbackCardClick = useCallback((item: ReviewFeedbackItem) => {
    setSelectedFeedbackKey(item.feedback_key);
    const targetUtteranceId = item.primary_utterance_id || item.utterance_id;
    if (targetUtteranceId) {
      setSelectedUtteranceId(targetUtteranceId);
      timelineRef.current?.scrollToUtterance(targetUtteranceId);
    }
  }, []);

  const handleUtteranceSelect = useCallback(
    (utterance: Utterance) => {
      setSelectedUtteranceId(utterance.id);
      const matches = feedbackByUtterance.get(utterance.id);
      if (matches && matches.length > 0) setSelectedFeedbackKey(matches[0]!.feedback_key);
    },
    [feedbackByUtterance],
  );

  return (
    <div className="flex flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* 左: 文字起こしタイムライン */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2">
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">
              {detail.session.display_name || detail.session.session_id}
            </span>
            {detail.session.client_name && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                {detail.session.client_name}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {detail.session.day}
              {detail.session.date ? ` / ${detail.session.date}` : ''}
            </span>
          </div>
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        </div>

        <TranscriptTimeline
          ref={timelineRef}
          blocks={timelineBlocks}
          selectedUtteranceId={selectedUtteranceId}
          viewMode={viewMode}
          onUtteranceSelect={handleUtteranceSelect}
          supportingUtteranceIdsWhitelist={activeSupportingUtteranceIds}
          feedbackIndicesByUtteranceId={feedbackIndicesByUtteranceId}
        />
      </div>

      {/* 右: 指摘一覧 */}
      <aside className="flex w-[26rem] flex-shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">指摘一覧</h3>
          <span className="text-xs text-gray-500">{feedbackItems.length} 件</span>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-3">
          {feedbackItems.map((item) => {
            const isSelected = item.feedback_key === selectedFeedbackKey;
            const displayName = item.item_name || item.item_id;
            const indexNumber = feedbackIndexByKey.get(item.feedback_key);
            return (
              <div
                key={item.feedback_key}
                className={`rounded-lg border transition-all ${
                  isSelected
                    ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-100'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleFeedbackCardClick(item)}
                  className="w-full p-3 text-left"
                >
                  <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {indexNumber !== undefined && (
                      <span className="rounded border border-red-200 bg-red-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-red-700">
                        #{indexNumber}
                      </span>
                    )}
                    <span className="font-mono">{item.timestamp}</span>
                    <span>{item.speaker === 'facilitator' ? 'コーチ' : '参加者'}</span>
                    {displayName && (
                      <span className="font-medium text-gray-700">{displayName}</span>
                    )}
                  </div>
                  {item.quote && (
                    <p
                      className={`mb-2 text-sm leading-snug text-gray-800 ${
                        isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-2'
                      }`}
                    >
                      「{item.quote}」
                    </p>
                  )}
                  {item.recommendation && (
                    <p
                      className={`text-xs leading-snug text-blue-700 ${
                        isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-3'
                      }`}
                    >
                      → {item.recommendation}
                    </p>
                  )}
                </button>
                {item.reason && (
                  <details className="-mt-1 px-3 pb-3">
                    <summary className="cursor-pointer list-none text-[11px] text-gray-500 hover:text-gray-700">
                      指摘理由を表示
                    </summary>
                    <p className="mt-1 text-xs leading-snug text-gray-600">{item.reason}</p>
                  </details>
                )}
              </div>
            );
          })}
          {feedbackItems.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
              指摘はありません
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
